import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { IonRouterOutlet, NavController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { filter, take } from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { EnvironmentCategory } from "src/components/shared-components/enum/enumEnvironment";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { PageSection } from "src/model/enum/enumPage";
import { LoggerService } from "./logger.service";
import { Location } from "@angular/common";
import { environment } from "src/environments/environment";

/**
 * Handle system navigation router
 */
@Injectable({
  providedIn: "root",
})
export class SystemRouterService {
  private _logContext: string = "System-router.service";

  /** Outlet of the app shell, registered by the AppComponent */
  private _rootOutlet: IonRouterOutlet = null;

  constructor(
    private _router: Router,
    private _loggerService: LoggerService,
    private _navController: NavController,
    private _location: Location,
    private _store: Store,
  ) { }

  /**
   * Register the outlet of the app shell, the source of {@link leavingPagesDestroyed$}.
   */
  public registerRootOutlet(outlet: IonRouterOutlet): void {
    this._rootOutlet = outlet;
  }

  /**
   * Emits when the previous page has really been destroyed after a navigation.
   *
   * When the promise of a navigateToX() settles, the previous page is still there: Ionic destroys it only
   * at the end of the page transition, and only then its ngOnDestroy() runs. Use this to do something
   * after that cleanup (e.g. the logout closes MQTT only after the pages have released their topics).
   *
   * If the browser tab is in background, the transition starts only when the tab is shown again, so this
   * emission comes at that moment.
   *
   * Subscribe BEFORE navigating, so that the emission cannot be missed.
   */
  public get leavingPagesDestroyed$(): Observable<unknown> {
    return this._rootOutlet ? this._rootOutlet.stackDidChange : of(null);
  }

  /**
   * Navigate to the splash page.
   *
   * @returns Promise settled when the router is done with the navigation: TRUE if a page has been left
   * (its destruction is signalled later by {@link leavingPagesDestroyed$}), FALSE if the navigation did not
   * happen or the application was already on the splash page.
   */
  public async navigateToSplash(): Promise<boolean> {
    const isAlreadyOnSplash: boolean = this._router.url === "/splash";

    const hasNavigated: boolean = await this._navController.navigateRoot("/splash");

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "navigateToHomepage()",
      "NAVIGATED",
    );

    return hasNavigated && !isAlreadyOnSplash;
  }

  /**
   * Navigate to app homepage
   *
   * @returns See {@link navigateToSplash}
   */
  public navigateToHomepage(): Promise<boolean> {
    return this.navigateToSplash();
  }

  /**
   * Navigate to digital place privacy policy
   */
  public navigateToPrivacyPolicy() {
    this._router.navigateByUrl("/privacy-policy");
  }

  /**
   * Navigate to digital place privacy policy
   */
  public navigateToCookiePolicy() {
    this._router.navigateByUrl("/cookie-policy");
  }

  /**
   * Navigate to digital place DPA
   */
  public navigateToDPA() {
    this._router.navigateByUrl("/dpa");
  }

  /**
   * Navigate to digital place conditions of service
   */
  public navigateToConditionsOfService() {
    this._router.navigateByUrl("/general-conditions");
  }

  /**
   * Navigate to My access page
   */
  public navigateToMyAccess(idArea): void {
    this._router.navigateByUrl("/reserved-area/" + idArea + "/my-access");
  }

  /**
   * Navigate to digital place login
   */
  public navigateToDigitalPlaceLogin(): void {
    this._router.navigateByUrl("/login", {
      replaceUrl: true,
    });
  }

  /**
   * Navigate to digital place invitation
   */
  public navigateToDigitalPlaceInvitation(): void {
    this._router.navigateByUrl("/reserved-area/invitations-awaiting");
  }

  /**
   * Navigate to associate smart component
   */
  public navigateToSmartComponentAssociation(): void {
    this._router.navigateByUrl("/reserved-area/associate-smart-component");
  }

  /**
   * Navigate to user associated
   * @param idEnvironment
   */
  public navigateToEnvironmentUsersAssociated(idArea, idEnvironment) {
    this._router.navigateByUrl(
      "/reserved-area/environment/" +
      idArea +
      "/" +
      idEnvironment +
      "/userAssociated",
    );
  }

  /**
   * Navigate to request pwd
   *
   * @param idPlant ID plant to navigate
   */
  public navigateToRequestChangePWD(): void {
    this._router.navigateByUrl("/request-change-pwd");
  }

  /**
   * Navigate to environments management
   */
  public navigateToDigitalPlaceManageEnvironments(idAreaLocal): void {
    this._router.navigateByUrl("/reserved-area/environments/" + idAreaLocal);
  }

  /**
   * Navigate to import readings page
   */
  public navigateToImportReadings(
    idAreaLocal: number,
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      `/reserved-area/environments/${idAreaLocal}/${idEnvironment}/import-readings`,
    );
  }

  public navigateToAssociateMaintainersAndSystem(idArea): void {
    this._router.navigateByUrl(
      "/reserved-area/environments/" + idArea + "/associate-maintainers-system",
    );
  }

  /**
   * Navigate to digital place list building
   */
  public navigateToDigitalPlaceListBuilding(): void {
    this._router.navigateByUrl("/reserved-area/building-list");
  }

  /**
   * Navigate to the page that lists the administrators waiting for approval.
   * It is reserved to the root user.
   */
  public navigateToTrialUsers(): void {
    this._router.navigateByUrl("/reserved-area/trial-users");
  }

  /**
   * True when the current route is the digital place building list
   * (the main "home" list page, where a "back to the list" action is pointless).
   */
  public isOnDigitalPlaceListBuilding(): boolean {
    return this._router.url.split("?")[0] === "/reserved-area/building-list";
  }

  /**
   * Navigate to create account
   */
  public navigateToCreateAccount(token?: string): void {
    const url = token ? `/create-account?token=${token}` : "/create-account";
    this._router.navigateByUrl(url);
  }

  /**
   * Navigate to digitalPlace Home
   */
  public navigateToDigitalPlaceHome(): void {
    this._router.navigateByUrl("/reserved-area/home");
  }

  /**
   * Navigate to digitalPlace user details
   */
  public navigateToDigitalPlaceUserDetails(): void {
    this._router.navigateByUrl("/reserved-area/profile");
  }

  /**
   * Navigate to digitalPlace Building Edit
   */
  public navigateToBuildingEdit(idBuilding: number): void {
    this._router.navigateByUrl("/reserved-area/edit-building/" + idBuilding);
  }

  /**
   * Navigate to digitalPlace Building Edit
   */
  public navigateToBuildingAdd(): void {
    this._router.navigateByUrl("/reserved-area/edit-building/add");
  }

  /**
   * Navigate to digital place environment edit
   * @param idEnvironment
   */
  public navigateToEnvironmentEdit(
    idArea: number,
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      "/reserved-area/edit-environment/" + idArea + "/" + idEnvironment,
    );
  }

  /**
   * Navigate to digital place add environment
   * @param idEnvironment
   */
  public navigateToAddEnvironment(idArea: number): void {
    this._router.navigateByUrl("/reserved-area/edit-environment/" + idArea);
  }

  /**
   * Navigate to digitalPlace invite user
   */
  public navigateToInviteUser(idBuilding: number): void {
    this._router.navigateByUrl("/reserved-area/invite-user/" + idBuilding);
  }

  /**
   * Navigate to smart component configuration section
   */
  public navigateToSmartComponentConfiguration(): void {
    this._router.navigateByUrl("/reserved-area/smart-component-configuration");
  }

  /**
   * Navigate to knx configuration page
   */
  public navigateToKnxConfiguration(): void {
    this._router.navigateByUrl("/reserved-area/knx-configuration");
  }

  /**
   * Navigate to digitalPlace view invited users
   * @param idBuilding
   */
  public navigateToViewInvitedUsers(idBuilding: number): void {
    this._router.navigateByUrl(
      "/reserved-area/view-invited-users/" + idBuilding,
    );
  }

  /**
   * Navigate to home reserved area.
   * It's used as default page when user is authenticated
   */
  public navigateToHomepageReservedArea(): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "navigateToHomepageReservedArea()",
      "START",
    );

    this._router.navigateByUrl(AppSettings.URI_HOME_RESERVED_AREA);
  }

  /**
   * Navigate to plant list local
   */
  public navigateToPlantListLocal(showBackButton: boolean = false): void {
    this._router.navigateByUrl(
      "/local/plant/list?showBackButton=" + showBackButton,
      { replaceUrl: true },
    );
  }

  /**
   * Navigate to specific plant login to perform login plant
   */
  public navigateToLoginPlant(): void {
    this._router.navigateByUrl("/local/plant/login", { replaceUrl: true });
  }

  /**
   * Navigate to setting to disable screen
   */
  public navigateToDisableScreenSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/settings/disable-screen-settings",
    );
  }

  /**
   * Navigate to setting for blackbox
   */
  public navigateToBlackBoxSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/settings/black-box-settings",
    );
  }

  /**
   * Navigate to specific plant
   *
   * @param idPlant ID plant to navigate
   */
  public navigateToSpecificPlant(idPlant: number): void {
    this._router.navigateByUrl("/local/plant/manual/" + idPlant);
  }

  /**
   * Navigate to plant sync server
   */
  public navigateToPlantSyncServer(): void {
    this._router.navigateByUrl("/local/plant/direct/sync");
  }

  /**
   * Navigate to full list of supervisions
   */
  public navigateToListSupervisions(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/supervisions",
    );
  }

  /**
   * Navigate to specific custom interface (in graph mode) by own id and id releated environment
   *
   * @param idSupervision ID custome interface to open
   * @param idEnvironment ID releated custom interface
   */
  public navigateToSupervisionGraph(
    idSupervision: number,
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      `/supervisions/${idSupervision}/${idEnvironment}/graph`,
    );
  }

  /**
   * Navigate to specific custom interface (in list mode) by own id and id releated environment
   *
   * @param idSupervision ID custome interface to open
   * @param idEnvironment ID releated custom interface
   */
  public navigateToSupervisionList(
    idSupervision: number,
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      `/supervisions/${idSupervision}/${idEnvironment}/list`,
    );
  }

  /**
   * Navigate to specific smart component in supervisions page
   *
   * @param idEnvironment ID of single smart component environment
   */
  public navigateToSupervisionSmartComponent(idEnvironment: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      `/supervisions/smartcomponents/${idEnvironment}`,
    );
  }

  /**
   * Navigate to graphic page of smart components
   *
   * @param idEnvironment ID of single smart component environment
   */
  public navigateToGraphicSupervisionSmartComponent(
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      `/supervisions/smartcomponents/${idEnvironment}/graph`,
    );
  }

  /**
   * Navigate to full list of scenarios
   */
  public navigateToListScenarios(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/programming/scenarios",
    );
  }

  /**
   * Navigate to page for editing/creating scenario
   */
  public navigateToScenariosEditor(idScenario: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/programming/scenarios/" + idScenario,
    );
  }

  /**
   * Navigate to gateway KNX
   */
  public navigateToGatewayKNX(instanceguid: string): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + `/gateway/${instanceguid}`,
    );
  }

  /**
   * Navigate to black box
   */
  public navigateToBlackBox() {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/black-box",
    );
  }

  /**
   * Navigate to full list of schedules
   */
  public navigateToListSchedules(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/programming/schedules",
    );
  }

  /**
   * navigate to schedule insert data
   *
   * @param idSchedule ID schedule to opemn
   */
  public navigateToScheduleEditData(idSchedule: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/programming/schedules/" +
      idSchedule,
    );
  }

  /**
   * Navigate to the weekly-planning list of the currently selected building.
   *
   * The list route (`weekly-planning/:idAreaLocal`) requires the building id, so the current
   * building is read one-shot from the store. Used by the "default homepage" dispatcher when the
   * user's configured landing page is the weekly-plannings list.
   */
  public navigateToListWeeklyPlanning(): void {
    this._store
      .select(selectedBuilding)
      .pipe(
        filter((building) => !!building?.idAreaLocal),
        take(1),
      )
      .subscribe((building) => {
        this.navigateToWeeklyPlanning(building.idAreaLocal);
      });
  }

  /**
   * Navigate to weekly planning configuration edit data
   *
   * @param idWeeklyPlanning ID weekly planning to edit
   */
  public navigateToWeeklyPlanningEditData(idWeeklyPlanning: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/programming/weekly-planning/" +
      idWeeklyPlanning,
    );
  }

  /**
   * Navigate to the weekly-planning list of a building.
   *
   * @param idBuilding ID of the building whose plannings are listed
   */
  public navigateToWeeklyPlanning(idAreaLocal: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/weekly-planning/" + idAreaLocal,
    );
  }

  /**
   * Navigate to current weekly planning test single day
   *
   * @param idWeeklyPlanning ID current wweekly planning
   */
  public navigateToWeeklyPlanningTestSingleDay(idWeeklyPlanning: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/programming/weekly-planning/test-single-day/" +
      idWeeklyPlanning,
    );
  }

  /**
   * Navigate to full list of user accesses.
   * It could be execute open door command based on releated accesses
   *
   * @param tabToOpen IEnvironment category tab to open
   */
  public navigateToListUserAccess(
    tabToOpen?: EnvironmentCategory,
  ): void {
    if (tabToOpen) {
      this._router.navigateByUrl(
        AppSettings.URI_PARENT_RESERVED_AREA +
        "/accesses?to=" +
        tabToOpen.toString(),
      ); //to = Tab to open
    } else {
      this._router.navigateByUrl(
        AppSettings.URI_PARENT_RESERVED_AREA + "/accesses",
      );
    }
  }

  /**
   * Navigate to configurated users qrcodes
   */
  public navigateToUserQrCodes(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/access-control/user-qr-codes",
    );
  }

  /**
   * Navigate to report html list
   */
  public navigateToReportHTML(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/configuration/report-html",
    );
  }

  /**
   * Navigate to spercific reprot HTML
   *
   * @param idReportHtml ID report HTML to open
   */
  public navigateToSpecificReportHTML(idReportHtml: number) {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/configuration/report-html/" +
      idReportHtml,
    );
  }

  /**
   * Navigate to payment generation
   */
  public navigateToPaymentGeneration(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/configuration/generation",
    );
  }

  /**
   * Navigate to report payment configuration
   */
  public navigateToReportPayment(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/report-payments",
    );
  }

  /**
   * Navigate to load control section
   */
  public navigateToLoadControl(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/configuration/load-control",
    );
  }

  /**
   * Navigate to Direct-access URL configuration section
   */
  public navigateToDirectAccessURLConfiguration(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/configuration/direct-access-url",
    );
  }

  /**
   * Navigate to address forward section
   */
  public navigateToMappingBtwAddresses(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/configuration/addresses/forward",
    );
  }

  /**
   * Navigate to report device valid list
   */
  public navigateToReportDeviceValidList(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/device-valid-lists",
    );
  }

  /**
   * Navigate to report device history trransit
   */
  public navigateToReportDeviceHistoryTransit(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/device-history-transit",
    );
  }

  /**
   * Navigate to report device history trransit
   */
  public navigateToReportHistoryAccess(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/access",
    );
  }

  /**
   * Navigate to cameera sections
   */
  public navigateToListCameras(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/video-cameras",
    );
  }

  /**
   * Navigate to push communication
   */
  public navigateToLogCommunicationPush(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/report/communication-push-my-history",
    );
  }

  /**
   * Navigate to access control fast checkin page
   */
  public navigateToAccessControlFastCheckIn(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/access-control/fast-checkin",
    );
  }

  /**
   * Navigate to access control scan and go function
   */
  public navigateToAccessControlScanAndGo(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/access-control/scan-go",
    );
  }

  /**
   * Navigate to access control read card page
   */
  public navigateToAccessControlReadCard(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/access-control/read-card",
    );
  }

  /**
   * Navigate to access control access history
   */
  public navigateToAccessControlAccessHistory(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/access-history",
    );
  }

  /**
   * Navigate to access control presence history
   */
  public navigateToAccessControlPresenceHistory(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/presence-history",
    );
  }

  /**
   * Navigate to list customer
   */
  public navigateToListCustomerStaff(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/access-control/customer-staff",
    );
  }

  /**
   * Navigate to previous page
   */
  public navigateBack(): void {
    this._location.back();
  }

  /**
   * Navigate to specific customer page by own id
   *
   * @param idCustomer ID customer to open
   */
  public navigateToSpecificCustomerStaff(idCustomer: number) {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/customer-staff/" +
      idCustomer,
    );
  }

  /**
   * Navigate to specific customer edit page by id
   *
   * @param idCustomer ID customer to open
   */
  public navigateToSpecificCustomerStaffEdit(idCustomer: number) {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/customer-staff/" +
      idCustomer +
      "/edit",
    );
  }

  /**
   * Navigate to list cards
   */
  public navigateToListCard(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/access-control/cards",
    );
  }

  /**
   * Navigate to filter-card page
   */
  public navigateToFilterCard(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/cards/filter-card",
    );
  }

  /**
   * Navigate to specific card details
   *
   * @param idCard ID card to loa detials
   * @param idCard ID card to loa detials
   */
  public navigateToCardDetail(
    idCard: number,
    backPageSection: PageSection = PageSection.ACCESSCONTROL_CARDS,
    tab?: number,
  ): void {
    let paramTab: string = "";
    if (tab > 0) {
      paramTab = "&tab=" + tab;
    }
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/cards/" +
      idCard +
      "/" +
      "details?bpages=" +
      backPageSection +
      paramTab,
    );
  }

  /**
   * Navigate to page of card notification
   */
  public navigateToCardNotifications(idCard: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/cards/" +
      idCard +
      "/notifications",
    );
  }

  /**
   * Navigate to page of card write section
   */
  public navigateToCardWrite(idCard: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/cards/" +
      idCard +
      "/write",
    );
  }

  /**
   * Navigate to alarms history
   */
  public navigateToAlarmsHistory(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/report/alarms-history",
    );
  }

  /**
   * Navigate to access control fast checkin page
   */
  public navigateToSpecificCustomerStaffCards(idCustomerStaff: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/customer-staff/" +
      idCustomerStaff +
      "/cards",
    );
  }

  /**
   * Navigate to system option settings page
   */
  public navigateToSystemOptionSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/settings",
    );
  }

  /**
   * Navigate to backup option settings page
   */
  public navigateToSystemBackupSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/backup-settings",
    );
  }

  /**
   * Navigate to backup option settings page
   */
  public navigateToSystemRestoreSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA + "/restore-settings",
    );
  }

  /**
   * Navigate to device advanced settings sub-page
   */
  public navigateToDeviceAdvancedSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/settings/device-advanced-settings",
    );
  }

  /**
   * Navigate to device network settings
   */
  public navigateToNetworkSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/settings/device-network-settings",
    );
  }
  /**
   * Navigate to device-integrated relays safety settings sub-page
   */
  public navigateToDeviceRelaySafetySettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/settings/device-relay-safety-settings",
    );
  }

  /**
   * Navigate to Access-Control-Mode settings' sub-page
   */
  public navigateToAccessControlModeSettings(): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/settings/access-control-mode-settings",
    );
  }

  /**
   * Navigate to Access-control assign Salto tag page
   * @param idCard id card to associate the Salto tag with
   */
  public navigateToAccessControlAssignSaltoTagPage(idCard: number): void {
    this._router.navigateByUrl(
      AppSettings.URI_PARENT_RESERVED_AREA +
      "/access-control/cards/" +
      idCard +
      "/salto",
    );
  }

  /**
   * Navigate to specific page by section value
   *
   * @param pageSection Page section enum value
   */
  public openPageByPageSection(pageSection: number): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "openPageByPageSection()",
      "Navigate to home-default: " + PageSection[pageSection],
    );

    switch (pageSection) {
      case PageSection.CONFIGURATION_LOADCONTROL:
        this.navigateToLoadControl();
        break;
      case PageSection.CONFIGURATION_REPORT_PAYMENT_GENERATION:
        this.navigateToPaymentGeneration();
        break;
      case PageSection.CONFIGURATION_REPORT_HTML:
        this.navigateToReportHTML();
        break;
      case PageSection.HISTORY_PAYMENT_REPORT:
        this.navigateToReportPayment();
        break;
      case PageSection.SCENARIONSCHEDULE_SCENARIOS:
        this.navigateToListScenarios();
        break;
      case PageSection.SCENARIONSCHEDULE_SCHEDULES:
        this.navigateToListSchedules();
        break;
      case PageSection.ACCESSES_ROOM:
        this.navigateToListUserAccess(EnvironmentCategory.ROOM);
        break;
      case PageSection.ACCESSES_SUITE:
        this.navigateToListUserAccess(EnvironmentCategory.SUITE);
        break;
      case PageSection.ACCESSES_JUNIOR_SUITE:
        this.navigateToListUserAccess(EnvironmentCategory.JUNIOR_SUITE);
        break;
      case PageSection.ACCESSES_SINGLE_ROOM:
        this.navigateToListUserAccess(EnvironmentCategory.SINGLE_ROOM);
        break;
      case PageSection.ACCESSES_DOUBLE_ROOM:
        this.navigateToListUserAccess(EnvironmentCategory.DOUBLE_ROOM);
        break;
      case PageSection.ACCESSES_TRIPLE_ROOM:
        this.navigateToListUserAccess(EnvironmentCategory.TRIPLE_ROOM);
        break;
      case PageSection.ACCESSES_FAMILY_ROOM:
        this.navigateToListUserAccess(EnvironmentCategory.FAMILY_ROOM);
        break;
      case PageSection.ACCESSES_DORMITORY:
        this.navigateToListUserAccess(EnvironmentCategory.DORMITORY);
        break;
      case PageSection.ACCESSES_COMMON:
        this.navigateToListUserAccess(EnvironmentCategory.COMMON_AREA);
        break;
      case PageSection.ACCESSES_ELEVATOR_FLOOR:
        this.navigateToListUserAccess(EnvironmentCategory.ELAVATOR_FLOOR);
        break;
      case PageSection.ACCESSES_CABINETS:
        this.navigateToListUserAccess(EnvironmentCategory.CABINETS);
        break;
      case PageSection.ACCESSES_SKI_DEPOSITIS:
        this.navigateToListUserAccess(EnvironmentCategory.SKI_DEPOSITS);
        break;
      case PageSection.ACCESSES_EXTERNAL_ACCESS:
        this.navigateToListUserAccess(EnvironmentCategory.EXTERNAL_ACCESS);
        break;
      case PageSection.QR_CODES:
        this.navigateToUserQrCodes();
        break;
      case PageSection.HISTORY_NAVIGATE_TO_DEVICE_VALID_LIST:
        this.navigateToReportDeviceValidList();
        break;
      case PageSection.WEEKLY_PLANNING:
        this.navigateToListWeeklyPlanning();
        break;
      case PageSection.SCAN_AND_GO:
        this.navigateToAccessControlScanAndGo();
        break;
      default:
        this.navigateToListSupervisions();
        break;
    }
  }
  /**
   * Navigate to List Gateway page
   *  @param idArea: The id of the selected condominium/ building
   */
  public navigateToListGatewayPage(idArea: number): void {
    this._router.navigateByUrl(`reserved-area/list-gateway/${idArea}`);
  }
  /**
   * Navigate to gateway configuration page
   * @param idArea: The id of the selected condominium/ building
   * @param idGateway: Id of the clicked gateway type
   */
  public navigateToGatewayConfigurationPage(
    idArea: number,
    idGateway: number,
  ): void {
    this._router.navigateByUrl(
      `reserved-area/gateway-configuration/${idArea}/${idGateway}`,
    );
  }

  /**
   * Navigate back to the plant list page in the case there is one plant and we navigated directly to the login page
   * instead of the default plant list page
   */
  public navigateBackToPlantListOnExistSinglePlant(): void {
    this._router.navigateByUrl("/local/plant/list?showBackButton=navigateBack");
  }

  /**
   * Navigate to the device management page consisting of all devices associated with a particular environment
   * @param idArea Id of the clicked building
   * @param idEnvironment Environment id
   */
  public navigateToEnvironmentManageDevices(
    idArea: number,
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      `/reserved-area/manage-devices/${idArea}/${idEnvironment}`,
    );
  }

  /**
   * Navigate to environment access management page
   * @param idArea
   * @param idEnvironment
   */
  public navigateToEnvironmentAccessManagement(
    idArea: number,
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      `/reserved-area/access-management/${idArea}/${idEnvironment}`,
    );
  }

  /**
   * Navigate to the mqtt configuration page for a particular environment
   * @param idDevice device Id
   * @param idEnvironment environment Id
   * @param buildingName building Name
   */
  public navigateToEnvironmentMQTTConfig(
    idDevice: number,
    idEnvironment?: number,
    buildingName?: string,
  ): void {
    const queryParams = [];
    if (idEnvironment) queryParams.push(`idEnvironment=${idEnvironment}`);
    if (buildingName) queryParams.push(`buildingName=${buildingName}`);

    const queryString =
      queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

    this._router.navigateByUrl(
      `/reserved-area/manage-devices/mqtt-configuration/${idDevice}${queryString}`,
    );
  }

  /**
   * Navigate to Digital place manage bills
   */
  public navigateToManageBills(): void {
    this._router.navigateByUrl(`/reserved-area/list-bills`);
  }

  /**
   * Navigate to Digital place manage residents
   */
  public navigateToManageResidents(): void {
    this._router.navigateByUrl(`/reserved-area/list-residents`);
  }

  /**
   * Navigate to Digital place manage quotes page
   */
  public navigateToManageQuotes(): void {
    this._router.navigateByUrl(`/reserved-area/list-quotes`);
  }

  /**
   * Navigate to document security page
   */
  public navigateToDocumentSecurity(): void {
    this._router.navigateByUrl(`/reserved-area/document-security`);
  }

  /**
   * Navigate to voting page
   */
  public navigateToVoting(): void {
    this._router.navigateByUrl(`/reserved-area/voting`);
  }

  /**
   * Navigate to Digital place manage expense note
   */
  public navigateToExpenseNote(): void {
    this._router.navigateByUrl(`/reserved-area/expenses-note`);
  }

  /**
   * Navigate to Digital place manage expense note
   */
  public navigateShowExpensesNote(idExpenseNote: number): void {
    this._router.navigateByUrl(
      `/reserved-area/manage-expenses-note?id=` + idExpenseNote,
    );
  }

  /**
   * Navigate to Digital place quotes details edit page
   * @param quoteAction define the action to be performed on the quote
   */
  public navigateToQuotesDetailsEdit(id?: number): void {
    const route = id
      ? `/reserved-area/list-quotes/quotes-details?id=${id}`
      : `/reserved-area/list-quotes/quotes-details`;

    this._router.navigateByUrl(route);
  }

  /**
   *  Navigate to Digital place bills details edit page
   * @param id
   */
  public navigateBillsDetailsEdit(id?: number): void {
    const billRoute = id
      ? `/reserved-area/list-bills/bills-details?id=${id}`
      : `/reserved-area/list-bills/bills-details`;

    this._router.navigateByUrl(billRoute);
  }

  public navigateUsersDetailsEdit(id?: number): void {
    const baseRoute = `/reserved-area/list-residents/residents-details`;
    const usersRoute = id ? "${baseRoute}?id=${id}" : baseRoute;

    this._router.navigateByUrl(usersRoute);
  }

  /**
   *  Navigate to Digital place bills details edit page
   * @param id
   */
  public navigateResidentsDetailsEdit(id?: number): void {
    const residentRoute = `/reserved-area/list-residents/residents-details${id ? `?id=${id}` : ""
      }`;
    this._router.navigateByUrl(residentRoute);
  }

  /**
   * Navigate to consumption management
   */
  public navigateToConsumptionManagement(): void {
    this._router.navigateByUrl(`/reserved-area/consumption-management`);
  }

  /**
   * Navigate to associate smart component with Excel page
   */
  public navigateToSmartComponentExcelAssociation(): void {
    this._router.navigateByUrl(
      `/reserved-area/smart-component-excel-association`,
    );
  }

  /**
   * Navigate to associate deviced with different environments
   * @param idArea
   * @param idEnvironment
   */
  public navigateToAssociateDevice(
    idArea: number,
    idEnvironment: number,
  ): void {
    this._router.navigateByUrl(
      `reserved-area/manage-devices/${idArea}/${idEnvironment}/associate-devices`,
    );
  }
}
